/**
 * Token gallery — every `ctx.ui` control and every `--os-*` scale on one
 * scrollable page.
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * The same logic as the Boids debug overlay. GPU and visual state is invisible
 * to tests: `node --test` can prove `diffThemeVars` removes a stale key, but
 * nothing in CI can prove a button still has a focus ring, that the switch knob
 * lands inside its track, or that `--os-surface-2` is still distinguishable from
 * `--os-surface-1` after a theme swap. So the module prints its own truth.
 *
 * Before this file, verifying the kit meant hunting controls across a dozen
 * apps — open Code for the tree and tabs, Settings for the switches, Trading for
 * the chips — and you would still miss `:focus-visible` on half of them, because
 * you cannot tab through a control you never found. The gallery is how a human
 * verifies the whole kit in one glance, and how the next person notices a
 * control that lost its focus ring.
 *
 * ── Two rules that make it un-driftable ──────────────────────────────────
 * 1. Token swatches are read from `getComputedStyle(:root)` at open time, and
 *    the NAMES are enumerated out of the live stylesheets — not from a list
 *    kept in this file. Add a token to `os.css` and it appears here; delete
 *    one and it disappears. A gallery with its own hand-copied token table
 *    would be wrong within a week and would then be worse than nothing.
 * 2. Hover / active / focus cannot be forced by CSS alone, so the pinned-state
 *    variants are GENERATED from the kit's own stylesheet: every rule in
 *    `ui-host.ts` whose selector carries `:hover` / `:active` / `:focus` /
 *    `:focus-visible` / `:focus-within` is re-emitted with that pseudo-class
 *    swapped for an `.os-gal-pin-*` class. The pinned column is therefore the
 *    real rule, not a copy of it. If a control's hover rule is deleted, its
 *    pinned swatch goes flat here too.
 *
 * ── Reachability ─────────────────────────────────────────────────────────
 * Deliberately NOT a dock app, a Settings pane or a keyboard chord: this is a
 * developer instrument and nothing in normal use should be able to land on it
 * by accident. `installTokenGallery()` (called once from `OsRoot`) installs a
 * dev command on `window`; open it from the browser console with
 *
 *     __arclogTokenGallery()
 *
 * and close it with Escape, the ✕, or `__arclogTokenGallery.close()`.
 *
 * ── House rule ───────────────────────────────────────────────────────────
 * Every selector in the sheet below is prefixed `.os-gal-`. This stylesheet is
 * injected into the document head, so a bare `button {}` here would restyle the
 * entire OS. The pinned-state classes are NEW names; nothing in the kit was
 * renamed to make this page work.
 */

import { uiApi } from "./api";
import { FALLBACK_THEME_VARS } from "./theme-host";

const KIT_STYLE_ID = "arclog-ui-kit-css";
const GAL_STYLE_ID = "arclog-token-gallery-css";
const PIN_STYLE_ID = "arclog-token-gallery-pins";

/** Singleton — two galleries would fight over the same Escape key. */
let open$: { close(): void } | null = null;

/* ── Pinned states, generated from the kit's own sheet ───────────────────── */

/**
 * Ordered longest-first: `:focus-visible` must not be matched as `:focus`
 * followed by the literal text `-visible`.
 */
const PIN_RE = /:focus-visible|:focus-within|:focus|:hover|:active/g;
const PIN_MAP: Record<string, string> = {
  ":focus-visible": ".os-gal-pin-focus",
  ":focus-within": ".os-gal-pin-focus-within",
  ":focus": ".os-gal-pin-focus",
  ":hover": ".os-gal-pin-hover",
  ":active": ".os-gal-pin-active",
};

function styleSheetById(id: string): CSSStyleSheet | null {
  const node = document.getElementById(id) as HTMLStyleElement | null;
  const sheet = node?.sheet as CSSStyleSheet | null | undefined;
  if (!sheet) return null;
  try {
    // Touch cssRules once: a cross-origin sheet throws here, and it is better
    // to find out now than halfway through building the page.
    void sheet.cssRules;
  } catch {
    return null;
  }
  return sheet;
}

/**
 * Re-emit every state rule of the kit with its pseudo-class swapped for a pin
 * class. The pin rule carries one more class than the original and is appended
 * later in the head, so it wins the cascade without `!important`.
 *
 * `@media`/`@keyframes` blocks are skipped: they are `CSSGroupingRule`, not
 * `CSSStyleRule`. The only state rules inside one are the reduced-motion
 * overrides, which the pinned column does not need.
 */
function buildPinCss(): { css: string; count: number } {
  const sheet = styleSheetById(KIT_STYLE_ID);
  if (!sheet) return { css: "", count: 0 };
  const out: string[] = [];
  for (const rule of Array.from(sheet.cssRules)) {
    if (!(rule instanceof CSSStyleRule)) continue;
    const sel = rule.selectorText;
    if (!PIN_RE.test(sel)) {
      PIN_RE.lastIndex = 0;
      continue;
    }
    PIN_RE.lastIndex = 0;
    const pinned = sel.replace(PIN_RE, (m) => PIN_MAP[m] || m);
    out.push(`${pinned}{${rule.style.cssText}}`);
  }
  return { css: out.join("\n"), count: out.length };
}

/* ── Token discovery ─────────────────────────────────────────────────────── */

export type GalToken = {
  name: string;
  /** Computed value from `:root`, or "" when nothing declares it. */
  value: string;
  /** False when the name came only from the theme fallback map. */
  declared: boolean;
};

/**
 * Every `--os-*` custom property declared on `:root` by any same-origin
 * stylesheet, unioned with the keys of `FALLBACK_THEME_VARS`.
 *
 * The union is the point: a key present in the fallback map but computing to ""
 * is a theme contract that `os.css` no longer honours, and it shows up here as
 * "not declared" rather than silently doing nothing.
 */
export function collectTokens(): GalToken[] {
  const declared = new Set<string>();
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = (sheet as CSSStyleSheet).cssRules;
    } catch {
      continue; // cross-origin sheet — nothing of ours lives there
    }
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSStyleRule)) continue;
      if (!/(^|,)\s*:root\s*(,|$)/.test(rule.selectorText)) continue;
      for (let i = 0; i < rule.style.length; i++) {
        const prop = rule.style.item(i);
        if (prop.startsWith("--os-")) declared.add(prop);
      }
    }
  }
  const names = new Set<string>([...declared, ...Object.keys(FALLBACK_THEME_VARS)]);
  const cs = getComputedStyle(document.documentElement);
  return [...names]
    .sort()
    .map((name) => ({
      name,
      value: cs.getPropertyValue(name).trim(),
      declared: declared.has(name),
    }));
}

/**
 * Tokens the kit READS (`var(--os-…)`) that nothing declares on `:root`. These
 * are not bugs — a `var()` with a literal fallback is how a colour becomes
 * theme-reachable before anyone has picked a value for it — but they are the
 * list of things a theme can move that this page cannot show a swatch for, so
 * the gallery names them out loud.
 */
export function undeclaredReads(declaredNames: Set<string>): string[] {
  const sheet = styleSheetById(KIT_STYLE_ID);
  if (!sheet) return [];
  const seen = new Set<string>();
  for (const rule of Array.from(sheet.cssRules)) {
    if (!(rule instanceof CSSStyleRule)) continue;
    const m = rule.style.cssText.match(/--os-[a-z0-9-]+/g);
    if (!m) continue;
    for (const name of m) if (!declaredNames.has(name)) seen.add(name);
  }
  return [...seen].sort();
}

/* ── Grouping + swatch kind ──────────────────────────────────────────────── */

type Group = { title: string; blurb: string; test: (n: string, v: string) => boolean };

const isLen = (v: string) => /^-?[\d.]+px$/.test(v);

const GROUPS: Group[] = [
  {
    title: "Space",
    blurb: "Gaps and padding. Nothing in the kit uses a bare px gap.",
    test: (n) => n.startsWith("--os-space-"),
  },
  {
    title: "Radius",
    blurb: "Corner radii. sm=chips/icons, md=controls, lg=popovers, xl=modals.",
    test: (n) => n.startsWith("--os-radius-"),
  },
  {
    title: "Type scale",
    blurb: "Font sizes only. The colour tokens named --os-text* are under Text below.",
    test: (n, v) => n.startsWith("--os-text-") && isLen(v),
  },
  {
    title: "Font families",
    blurb: "UI and mono stacks.",
    test: (n) => n.startsWith("--os-font"),
  },
  {
    title: "Elevation",
    blurb: "Shadows. 1=resting, card=resting at card size, 2=floating, 3=modal.",
    test: (n) => n.startsWith("--os-elev-") || n.startsWith("--os-shadow-"),
  },
  {
    title: "Motion",
    blurb: "Durations and easings. The dots move on the token they are labelled with.",
    test: (n) => n.startsWith("--os-dur-") || n.startsWith("--os-ease-"),
  },
  {
    title: "Blur",
    blurb: "backdrop-filter radii. Each tile is blurring the checkerboard behind it.",
    test: (n) => n.startsWith("--os-blur-") || n === "--os-widget-blur",
  },
  {
    title: "Borders",
    blurb: "1px hairlines. subtle=dividers, default=controls, strong=emphasis.",
    test: (n) => n.startsWith("--os-border") || n === "--os-glass-border",
  },
  {
    title: "Surfaces",
    blurb:
      "White tints that sit ON the pane behind them — they never go darker. The exception is --os-surface-solid, the opaque band a sticky header needs.",
    test: (n) =>
      n.startsWith("--os-surface-") ||
      n === "--os-hover" ||
      n === "--os-active" ||
      n.startsWith("--os-glass-"),
  },
  {
    title: "Wells",
    blurb: "The ONLY two tokens that go darker than their parent. Code panes, tab strips, terminals.",
    test: (n) => n.startsWith("--os-well-"),
  },
  {
    title: "Window controls",
    blurb: "Traffic lights. Flat fills, no gradients.",
    test: (n) => n.startsWith("--os-ctl-"),
  },
  {
    title: "Dock tints",
    blurb: "Flat per-app dock/icon tints. Pick one; do not invent a seventh.",
    test: (n) => n.startsWith("--os-tint-"),
  },
  {
    title: "Text",
    blurb:
      "Foreground colours. Dim = secondary, faint = placeholder, danger = the softer red that is read rather than the one that acts.",
    test: (n) => n === "--os-text" || n.startsWith("--os-text-"),
  },
  {
    title: "Semantic colour",
    blurb: "Accent and status. Read these instead of re-picking a blue or a red.",
    test: (n) =>
      n === "--os-accent" ||
      n === "--os-accent-soft" ||
      n === "--os-danger" ||
      n === "--os-warn" ||
      /* Amber sits between warn and danger. Without this line it falls through
         to the catch-all group and gets swatched as chrome metrics. */
      n === "--os-caution" ||
      n === "--os-success" ||
      n === "--os-ok" ||
      n === "--os-bg" ||
      n === "--os-window-body",
  },
  {
    title: "Scrollbars",
    blurb: "Global scrollbar chrome — themes reach these too.",
    test: (n) => n.startsWith("--os-scroll-"),
  },
  {
    title: "Chrome metrics & host-owned",
    blurb:
      "Everything else: bar heights, grid steps, widget geometry. --os-screen-w/h and --os-ui-scale are written inline on <html> by the shell — a theme must not set them.",
    test: () => true, // catch-all, must stay last
  },
];

type SwatchKind = "space" | "radius" | "type" | "font" | "shadow" | "motion" | "blur" | "color" | "raw";

function swatchKind(name: string, value: string): SwatchKind {
  if (name.startsWith("--os-font")) return "font";
  if (name.startsWith("--os-space-")) return "space";
  if (name.startsWith("--os-radius-") && isLen(value)) return "radius";
  if (name.startsWith("--os-text-") && isLen(value)) return "type";
  if (name.startsWith("--os-blur-") || name === "--os-widget-blur") return "blur";
  if (/^-?[\d.]+m?s$/.test(value)) return "motion";
  if (/^cubic-bezier|^(linear|ease)/.test(value)) return "motion";
  if (/^(inset\s)?-?[\d.]+px\s/.test(value) && /rgba?\(|#/.test(value)) return "shadow";
  if (/^(#|rgba?\(|hsla?\(|transparent$|currentColor$)/i.test(value)) return "color";
  return "raw";
}

/* ── Stylesheet ──────────────────────────────────────────────────────────── */

const GAL_CSS = `
.os-gal-scrim{position:fixed;inset:0;z-index:900;background:var(--os-bg,#05060c);color:var(--os-text,#fff);font:var(--os-text-base) var(--os-font-ui);display:flex;flex-direction:column}
.os-gal-head{display:flex;align-items:center;gap:var(--os-space-4);padding:var(--os-space-4) var(--os-space-6);border-bottom:1px solid var(--os-border-subtle);background:var(--os-surface-1);flex:none}
.os-gal-title{font-size:var(--os-text-xl);font-weight:650;letter-spacing:-.01em}
.os-gal-sub{font-size:var(--os-text-sm);opacity:.55;flex:1;min-width:0}
.os-gal-body{flex:1;min-height:0;overflow:auto;padding:var(--os-space-6);display:flex;flex-direction:column;gap:var(--os-space-6)}
.os-gal-sec{display:flex;flex-direction:column;gap:var(--os-space-4)}
.os-gal-h2{font-size:var(--os-text-lg);font-weight:650;letter-spacing:-.01em;border-bottom:1px solid var(--os-border-subtle);padding-bottom:var(--os-space-2)}
.os-gal-note{font-size:var(--os-text-sm);opacity:.55;max-width:78ch;line-height:1.5}
.os-gal-warn{font-size:var(--os-text-sm);color:var(--os-warn);border:1px solid var(--os-border-default);border-radius:var(--os-radius-md);padding:var(--os-space-3) var(--os-space-4);background:var(--os-surface-1)}
.os-gal-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:var(--os-space-4)}
.os-gal-card{border:1px solid var(--os-border-subtle);border-radius:var(--os-radius-lg);background:var(--os-surface-1);padding:var(--os-space-4);display:flex;flex-direction:column;gap:var(--os-space-3);min-width:0}
.os-gal-name{font:var(--os-text-sm) var(--os-font-mono);opacity:.9;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-gal-val{font:var(--os-text-xs) var(--os-font-mono);opacity:.5;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-gal-missing{color:var(--os-danger);opacity:.9}
.os-gal-demo{min-height:44px;display:flex;align-items:center;gap:var(--os-space-3);min-width:0}
/* Checkerboard: the only way to SEE a blur radius or an alpha is to put
   something busy behind it. */
.os-gal-checks{background-image:linear-gradient(45deg,rgba(255,255,255,.22) 25%,transparent 25%,transparent 75%,rgba(255,255,255,.22) 75%),linear-gradient(45deg,rgba(255,255,255,.22) 25%,transparent 25%,transparent 75%,rgba(255,255,255,.22) 75%);background-size:14px 14px;background-position:0 0,7px 7px;background-color:var(--os-accent)}
.os-gal-bar{height:14px;border-radius:3px;background:var(--os-accent);flex:none}
.os-gal-box{width:64px;height:44px;background:var(--os-surface-3);border:1px solid var(--os-border-default);flex:none}
.os-gal-elev{width:100%;height:44px;border-radius:var(--os-radius-md);background:var(--os-window-body)}
.os-gal-blur{position:relative;width:100%;height:56px;border-radius:var(--os-radius-md);overflow:hidden}
.os-gal-blur-fg{position:absolute;inset:8px;border-radius:var(--os-radius-sm);border:1px solid var(--os-border-default);background:rgba(0,0,0,.18)}
.os-gal-swatch{width:100%;height:44px;border-radius:var(--os-radius-md);border:1px solid var(--os-border-subtle)}
.os-gal-onwell{border-radius:var(--os-radius-md);padding:var(--os-space-3);background:var(--os-window-body)}
.os-gal-track{position:relative;flex:1;min-width:0;height:14px;border-radius:999px;background:var(--os-surface-2)}
.os-gal-dot{position:absolute;top:1px;left:1px;width:12px;height:12px;border-radius:50%;background:var(--os-accent);transform:translateX(0)}
.os-gal-motion-on .os-gal-dot{transform:translateX(var(--os-gal-throw,120px))}
.os-gal-rule{display:flex;flex-direction:column;gap:2px}
/* Control matrix: one row per control, one labelled cell per state. */
.os-gal-matrix{display:flex;flex-wrap:wrap;gap:var(--os-space-5);align-items:flex-start}
.os-gal-cell{display:flex;flex-direction:column;gap:var(--os-space-2);min-width:0}
.os-gal-cap{font:var(--os-text-xs) var(--os-font-mono);opacity:.45;text-transform:uppercase;letter-spacing:.06em}
.os-gal-stage{border:1px dashed var(--os-border-subtle);border-radius:var(--os-radius-md);padding:var(--os-space-4);background:var(--os-window-body);min-width:0}
.os-gal-stage-tall{height:220px;display:flex;flex-direction:column;min-height:0}
.os-gal-w280{width:280px}
.os-gal-w360{width:360px}
.os-gal-w520{width:520px}
`;

/* ── Small builders ──────────────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  cls?: string,
  text?: string
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

function section(title: string, note?: string): HTMLElement {
  const s = el("section", "os-gal-sec");
  s.appendChild(el("h2", "os-gal-h2", title));
  if (note) s.appendChild(el("p", "os-gal-note", note));
  return s;
}

function cell(caption: string, node: Node, stageCls = ""): HTMLElement {
  const c = el("div", "os-gal-cell");
  c.appendChild(el("div", "os-gal-cap", caption));
  const stage = el("div", ("os-gal-stage " + stageCls).trim());
  stage.appendChild(node);
  c.appendChild(stage);
  return c;
}

/** A control row: the live control, then one pinned copy per forced state. */
function matrix(
  title: string,
  make: () => HTMLElement,
  states: Array<{ caption: string; pin?: string; disable?: boolean }>
): HTMLElement {
  const wrap = el("div", "os-gal-sec");
  wrap.appendChild(el("div", "os-gal-cap", title));
  const row = el("div", "os-gal-matrix");
  for (const st of states) {
    const node = make();
    if (st.pin) node.classList.add(st.pin);
    if (st.disable) {
      const target = node as HTMLElement & { disabled?: boolean };
      if ("disabled" in target) target.disabled = true;
      node.querySelectorAll("button,input,select,textarea").forEach((n) => {
        (n as HTMLInputElement).disabled = true;
      });
    }
    row.appendChild(cell(st.caption, node));
  }
  wrap.appendChild(row);
  return wrap;
}

const FIVE = [
  { caption: "default" },
  { caption: "hover", pin: "os-gal-pin-hover" },
  { caption: "active", pin: "os-gal-pin-active" },
  { caption: "focus-visible", pin: "os-gal-pin-focus" },
  { caption: "disabled", disable: true },
];

/* ── Sections ────────────────────────────────────────────────────────────── */

function buildControls(destroyers: Array<() => void>): HTMLElement {
  const sec = section(
    "ctx.ui controls",
    "Each row is one control in five states. The first cell is live — hover it, click it, tab to it. " +
      "The rest are pinned with .os-gal-pin-* classes generated from the kit's own hover/active/focus rules, " +
      "so what you see is the real rule and not a copy of it. Every control here must show a visible " +
      "focus-visible ring and must present a hit target of at least 32px."
  );

  /* buttons — four variants × five states */
  for (const variant of ["default", "primary", "danger", "ghost"] as const) {
    sec.appendChild(
      matrix(
        `button — variant "${variant}"`,
        () => uiApi.button({ label: "Publish", icon: "upload", variant }),
        FIVE
      )
    );
  }

  /* form controls that expose .el */
  const valueControls: Array<[string, () => { el: HTMLElement; destroy(): void }]> = [
    ["textField", () => uiApi.textField({ label: "Name", value: "hello", placeholder: "Type…" })],
    ["numberField", () => uiApi.numberField({ label: "Columns", value: 4, min: 1, max: 12 })],
    ["select", () => uiApi.select({ label: "Mode", options: [{ value: "a", label: "Automatic" }, { value: "b", label: "Manual" }], value: "a" })],
    ["slider", () => uiApi.slider({ label: "Opacity", min: 0, max: 100, value: 62, unit: "%" })],
    ["colorPicker", () => uiApi.colorPicker({ label: "Tint", value: "#0a84ff", swatches: ["#0a84ff", "#bf5af2", "#ff9f0a", "#30d158"] })],
    ["toggle", () => uiApi.toggle({ label: "Reduce motion", checked: true })],
  ];
  for (const [name, make] of valueControls) {
    sec.appendChild(
      matrix(
        name,
        () => {
          const h = make();
          destroyers.push(() => h.destroy());
          return h.el;
        },
        FIVE
      )
    );
  }

  /* The <button aria-pressed> switch. Same rule set as .os-field-switch, but a
     different class name, and modules select on both — so both are shown. */
  sec.appendChild(
    matrix(
      ".os-switch (aria-pressed variant of the same switch)",
      () => {
        const b = el("button", "os-switch on");
        b.type = "button";
        b.setAttribute("aria-pressed", "true");
        return b;
      },
      FIVE
    )
  );

  /* Segmented control + stepper — used by the editor prefs modal. */
  sec.appendChild(
    matrix(
      ".os-seg (segmented)",
      () => {
        const g = el("div", "os-seg");
        for (const [i, lab] of ["Off", "Soft", "Hard"].entries()) {
          const b = el("button", i === 1 ? "on" : "");
          b.type = "button";
          b.textContent = lab;
          g.appendChild(b);
        }
        return g;
      },
      [{ caption: "default" }, { caption: "disabled", disable: true }]
    )
  );
  sec.appendChild(
    matrix(
      ".os-step (stepper)",
      () => {
        const g = el("div", "os-step");
        const minus = el("button", "", "−");
        minus.type = "button";
        const v = el("span", "v", "13");
        const plus = el("button", "", "+");
        plus.type = "button";
        g.append(minus, v, plus);
        return g;
      },
      [{ caption: "default" }, { caption: "disabled", disable: true }]
    )
  );

  /* Host-mounted controls — one instance each, live only. */
  const hosted = el("div", "os-gal-matrix");

  const treeHost = el("div", "os-gal-w280");
  const tree = uiApi.tree(treeHost, {});
  tree.setData([
    {
      id: "src",
      label: "src",
      icon: "folder",
      expanded: true,
      children: [
        { id: "src/index.ts", label: "index.ts", icon: "file" },
        { id: "src/ui.ts", label: "ui.ts", icon: "file" },
      ],
    },
    { id: "readme", label: "README.md", icon: "file" },
  ]);
  tree.setSelected("src/ui.ts");
  destroyers.push(() => tree.destroy());
  hosted.appendChild(cell("tree — live (row hover/active/selected, twist focus)", treeHost));

  const ssHost = el("div", "os-gal-w280");
  const ss = uiApi.searchSelect(ssHost, {
    options: [
      { value: "a", label: "anthropic/claude", hint: "200k", group: "Frontier" },
      { value: "b", label: "openai/gpt", hint: "128k", group: "Frontier" },
      { value: "c", label: "local/llama", hint: "8k", group: "Local" },
    ],
    value: "a",
  });
  destroyers.push(() => ss.destroy());
  hosted.appendChild(cell("searchSelect — live (click to open the popover)", ssHost));

  const sbHost = el("div", "os-gal-w360");
  const sb = uiApi.statusBar(sbHost, {
    items: [
      { id: "f", label: "index.ts", icon: "file" },
      { id: "p", label: "Ln 12, Col 4" },
      { id: "b", label: "Build OK", tone: "ok", align: "right", onClick: () => {} },
      { id: "w", label: "2 warnings", tone: "warn", align: "right" },
    ],
  });
  destroyers.push(() => sb.destroy());
  hosted.appendChild(cell("statusBar — live (26px bar; the one place the 32px floor is waived)", sbHost));

  const tabsHost = el("div", "os-gal-w360");
  const tabs = uiApi.tabs(tabsHost, {
    tabs: [
      { id: "1", label: "index.ts", icon: "file" },
      { id: "2", label: "ui-host.ts", icon: "file", dirty: true },
      { id: "3", label: "os.css", icon: "file" },
    ],
    activeId: "1",
    onSelect: () => {},
    onClose: () => {},
  });
  destroyers.push(() => tabs.destroy());
  hosted.appendChild(cell("tabs — live (dot↔× swap on hover AND on close-button focus)", tabsHost));

  const sideHost = el("div", "os-gal-w280 os-gal-stage-tall");
  const side = uiApi.sidebar(sideHost, {
    items: [
      { id: "g", label: "General", icon: "settings", group: "System" },
      { id: "d", label: "Display", icon: "monitor", group: "System", hint: "⌘2" },
      { id: "x", label: "Packages", icon: "package", group: "Apps", disabled: true },
    ],
    value: "g",
  });
  destroyers.push(() => (side as { destroy?: () => void }).destroy?.());
  hosted.appendChild(cell("sidebar — live (is-on / is-disabled / focus ring)", sideHost));

  const aciHost = el("div", "os-gal-w360");
  uiApi.agentChatInput(aciHost, {
    placeholder: "Message the agent…",
    actions: [{ id: "a", icon: "paperclip", title: "Attach", onClick: () => {} }],
    mode: { label: "claude-opus", icon: "sparkles", onClick: () => {} },
  });
  hosted.appendChild(cell("agentChatInput — live (wrapper carries the ring on focus-within)", aciHost));

  const codeHost = el("div", "os-gal-w360 os-gal-stage-tall");
  const code = uiApi.codeEditor(codeHost, {
    value: '// comment\nconst n = 42;\nconst s = "string";\n',
    language: "javascript",
  });
  destroyers.push(() => (code as { destroy?: () => void }).destroy?.());
  hosted.appendChild(cell("codeEditor — live (well + inset ring + --os-code-* token colours)", codeHost));

  const rteHost = el("div", "os-gal-w360 os-gal-stage-tall");
  uiApi.richText(rteHost, { html: "<h2>Rich text</h2><p>Toolbar buttons are 32×32.</p>" });
  hosted.appendChild(cell("richText — live (toolbar buttons, inset body ring)", rteHost));

  const splitHost = el("div", "os-gal-w520 os-gal-stage-tall");
  const a = el("div", "os-gal-onwell", "pane A");
  const b = el("div", "os-gal-onwell", "pane B");
  const sp = uiApi.split(splitHost, [a, b], { direction: "horizontal", sizes: [1, 2] });
  destroyers.push(() => sp.destroy());
  hosted.appendChild(cell("split — live (5px gutter: a drag affordance, exempt from the 32px floor)", splitHost));

  sec.appendChild(hosted);

  /* Overlays cannot live inline — they mount to <body>. Buttons open them. */
  const overlays = el("div", "os-gal-matrix");
  const palette = uiApi.commandPalette({
    hotkey: false,
    commands: [
      { id: "1", label: "Reload window", group: "OS", shortcut: "⌘R", run: () => {} },
      { id: "2", label: "Toggle sidebar", group: "View", hint: "editor", run: () => {} },
      { id: "3", label: "Disabled command", group: "View", disabled: true, run: () => {} },
    ],
  });
  destroyers.push(() => palette.destroy());
  overlays.appendChild(
    cell(
      "commandPalette — mounts to <body>",
      uiApi.button({ label: "Open command palette", icon: "search", onClick: () => palette.open() })
    )
  );
  overlays.appendChild(
    cell(
      "modal (.os-modal / .os-pref-row) — mounts to <body>",
      uiApi.button({ label: "Open editor prefs modal", icon: "settings", onClick: () => uiApi.editorPrefs.open() })
    )
  );
  overlays.appendChild(
    cell(
      "tooltip — hover the target",
      (() => {
        const t = uiApi.button({ label: "Hover me", icon: "info" });
        uiApi.tooltip.set(t, "Tooltips are host-owned chrome");
        return t;
      })()
    )
  );
  sec.appendChild(overlays);

  sec.appendChild(
    el(
      "p",
      "os-gal-note",
      "Not covered, and why: ctx.ui.selectable and ctx.ui.editorKeys attach behaviour and paint nothing; " +
        "ctx.ui.markdown is text rendering with no control states; the scrim/backdrop of the palette and " +
        "the modal is only visible while that overlay is open, so it is shown by opening it rather than inline."
    )
  );
  return sec;
}

function buildTokens(tokens: GalToken[], timers: number[]): HTMLElement {
  const sec = section(
    "Token scales",
    "Names are enumerated from the live stylesheets; values are read from getComputedStyle(:root) at the " +
      "moment this page opened. Switch theme and reopen to see a theme's values. A token shown in red is " +
      "named by the theme fallback map but declared nowhere, which means themes can set it and nothing reads it."
  );

  const remaining = [...tokens];
  for (const g of GROUPS) {
    const mine: GalToken[] = [];
    for (let i = remaining.length - 1; i >= 0; i--) {
      if (g.test(remaining[i].name, remaining[i].value)) mine.unshift(...remaining.splice(i, 1));
    }
    if (!mine.length) continue;
    const block = el("div", "os-gal-sec");
    block.appendChild(el("div", "os-gal-cap", `${g.title} — ${mine.length}`));
    block.appendChild(el("p", "os-gal-note", g.blurb));
    const grid = el("div", "os-gal-grid");
    for (const t of mine) grid.appendChild(tokenCard(t, timers));
    block.appendChild(grid);
    sec.appendChild(block);
  }
  return sec;
}

function tokenCard(t: GalToken, timers: number[]): HTMLElement {
  const card = el("div", "os-gal-card");
  const demo = el("div", "os-gal-demo");
  const kind = swatchKind(t.name, t.value);
  const v = `var(${t.name})`;

  if (!t.value) {
    demo.appendChild(el("div", "os-gal-val os-gal-missing", "not declared on :root"));
  } else if (kind === "space") {
    const bar = el("div", "os-gal-bar");
    bar.style.width = v;
    demo.appendChild(bar);
  } else if (kind === "radius") {
    const box = el("div", "os-gal-box");
    box.style.borderRadius = v;
    demo.appendChild(box);
  } else if (kind === "type") {
    const s = el("div", "", "Ag 123");
    s.style.fontSize = v;
    s.style.lineHeight = "1.3";
    demo.appendChild(s);
  } else if (kind === "font") {
    const s = el("div", "", "Ag 0123 — the quick brown fox");
    s.style.font = `13px ${t.value}`;
    demo.appendChild(s);
  } else if (kind === "shadow") {
    const s = el("div", "os-gal-elev");
    s.style.boxShadow = v;
    demo.appendChild(s);
  } else if (kind === "blur") {
    const wrap = el("div", "os-gal-blur os-gal-checks");
    const fg = el("div", "os-gal-blur-fg");
    fg.style.backdropFilter = `blur(${t.value})`;
    (fg.style as CSSStyleDeclaration & { webkitBackdropFilter?: string }).webkitBackdropFilter =
      `blur(${t.value})`;
    wrap.appendChild(fg);
    demo.appendChild(wrap);
  } else if (kind === "motion") {
    const track = el("div", "os-gal-track");
    const dot = el("div", "os-gal-dot");
    const isDur = /m?s$/.test(t.value);
    dot.style.transitionProperty = "transform";
    dot.style.transitionDuration = isDur ? v : "var(--os-dur-slow)";
    dot.style.transitionTimingFunction = isDur ? "var(--os-ease-out)" : v;
    track.appendChild(dot);
    demo.appendChild(track);
    const tick = window.setInterval(() => {
      track.style.setProperty("--os-gal-throw", `${Math.max(0, track.clientWidth - 14)}px`);
      track.classList.toggle("os-gal-motion-on");
    }, 900);
    timers.push(tick);
  } else if (kind === "color") {
    // Colours are alphas more often than not, so they are painted over the
    // opaque window body — the same surface they sit on in a real app.
    const well = el("div", "os-gal-onwell");
    const sw = el("div", "os-gal-swatch");
    sw.style.background = v;
    if (t.name.startsWith("--os-border") || t.name === "--os-glass-border") {
      sw.style.background = "transparent";
      sw.style.border = `1px solid ${v}`;
    }
    well.appendChild(sw);
    demo.appendChild(well);
  } else {
    demo.appendChild(el("div", "os-gal-val", t.value));
  }

  card.append(demo, el("div", "os-gal-name", t.name), el("div", "os-gal-val", t.value || "—"));
  return card;
}

/* ── Open / close ────────────────────────────────────────────────────────── */

export function closeTokenGallery(): void {
  open$?.close();
}

export function openTokenGallery(): void {
  if (typeof document === "undefined") return;
  if (open$) return;

  // The kit sheet has to exist before the pinned states can be derived from it.
  uiApi.ensureCss();

  if (!document.getElementById(GAL_STYLE_ID)) {
    const s = document.createElement("style");
    s.id = GAL_STYLE_ID;
    s.textContent = GAL_CSS;
    document.head.appendChild(s);
  }
  // Rebuilt on every open: a theme switch (or an edit in dev) can change the
  // kit sheet under us, and a stale pin sheet would be a lie.
  document.getElementById(PIN_STYLE_ID)?.remove();
  const pins = buildPinCss();
  const pinNode = document.createElement("style");
  pinNode.id = PIN_STYLE_ID;
  pinNode.textContent = pins.css;
  document.head.appendChild(pinNode);

  const tokens = collectTokens();
  const declaredNames = new Set(tokens.filter((t) => t.declared).map((t) => t.name));
  const reads = undeclaredReads(declaredNames);

  const destroyers: Array<() => void> = [];
  const timers: number[] = [];

  const scrim = el("div", "os-gal-scrim");
  const head = el("div", "os-gal-head");
  head.appendChild(el("div", "os-gal-title", "Token gallery"));
  head.appendChild(
    el(
      "div",
      "os-gal-sub",
      `${tokens.length} tokens · ${pins.count} pinned state rules · Esc to close`
    )
  );
  const closeBtn = uiApi.button({ label: "Close", icon: "x", variant: "ghost" });
  head.appendChild(closeBtn);
  const body = el("div", "os-gal-body");

  if (!pins.count) {
    body.appendChild(
      el(
        "div",
        "os-gal-warn",
        "Could not read the ctx.ui stylesheet, so the pinned hover/active/focus columns are empty. " +
          "Every state cell below therefore shows the default state. Do not read this page as a pass."
      )
    );
  }

  body.appendChild(buildControls(destroyers));
  body.appendChild(buildTokens(tokens, timers));

  if (reads.length) {
    const s = section(
      "Read but not declared",
      "The kit reads these with a literal fallback, so a theme that declares one takes effect immediately — " +
        "but :root declares none of them, so this page cannot show a swatch and Settings → Themes cannot " +
        "round-trip them. Each is a candidate for a real token."
    );
    const grid = el("div", "os-gal-grid");
    for (const name of reads) {
      const c = el("div", "os-gal-card");
      c.appendChild(el("div", "os-gal-name", name));
      c.appendChild(el("div", "os-gal-val os-gal-missing", "no :root declaration"));
      grid.appendChild(c);
    }
    s.appendChild(grid);
    body.appendChild(s);
  }

  scrim.append(head, body);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") close();
  };

  function close(): void {
    document.removeEventListener("keydown", onKey);
    for (const t of timers) clearInterval(t);
    for (const d of destroyers) {
      try {
        d();
      } catch {
        /* one control failing to tear down must not strand the rest */
      }
    }
    scrim.remove();
    document.getElementById(PIN_STYLE_ID)?.remove();
    open$ = null;
  }

  closeBtn.addEventListener("click", close);
  document.addEventListener("keydown", onKey);
  document.body.appendChild(scrim);
  open$ = { close };
}

/* ── Dev command ─────────────────────────────────────────────────────────── */

type GalleryCommand = { (): void; close(): void };

/**
 * Install the console command. Called once from `OsRoot`; installing it does
 * not render anything, so the gallery costs nothing until someone asks for it.
 */
export function installTokenGallery(): void {
  if (typeof window === "undefined") return;
  const cmd = (() => openTokenGallery()) as GalleryCommand;
  cmd.close = closeTokenGallery;
  (window as unknown as Record<string, unknown>).__arclogTokenGallery = cmd;
}
